import React, { useEffect, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { useParams, useHistory } from "react-router-dom";
import toastr from "toastr";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import LinearProgress from "@material-ui/core/LinearProgress";
import Fab from "@material-ui/core/Fab";
import Divider from "@material-ui/core/Divider";

import Box from "@material-ui/core/Box";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";

import Card from "components/Card/Card";
import CardHeader from "components/Card/CardHeader";
import CardBody from "components/Card/CardBody";
import CardText from "components/Card/CardText";

import DescriptionIcon from "@material-ui/icons/Description";

import Danger from "components/Typography/Danger";
import Button from "components/CustomButtons/Button";

import { actionCreators as actionCreatorsChapter } from "../../../_actions/chapterActions";
import { currentYear, yearList } from "../../../utils";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%"
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2)
  },
  marginTop: {
    marginTop: theme.spacing(3)
  },
  marginBottom: {
    marginBottom: theme.spacing(3)
  },
  fullWidth: {
    width: "100%"
  },
  fileUpload: {
    display: "none"
  },
  cursorPointer: {
    cursor: "pointer"
  }
}));

const ChapterBudgetForm = ({
  isUpdating,
  getChapterBudget,
  activeChapterBudget,
  chapterBudgets,
  isFetching,
  isFetched,
  createChapterBudget,
  updateChapterBudget,
  updateChapterBudgetFile,
  downloadChapterBudgetFile,
  user
}) => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const [budget, setBudget] = useState(null);
  const [notes, setNotes] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);

  const isCreate = id === "add";

  const [fiscalYear, setFiscalYear] = useState();

  const filteredYearList = useMemo(() => {
    let a = new Set(yearList);
    let b = new Set(chapterBudgets?.map(row => row.year));
    let years = Array.from(new Set([...a].filter(x => !b.has(x))));
    setFiscalYear(years[0]);
    return years;
  }, [chapterBudgets]);

  useEffect(() => {
    if (!isCreate) {
      getChapterBudget(id);
    }
  }, [id]);

  useEffect(() => {
    if (activeChapterBudget) {
      setBudget(activeChapterBudget);
      setNotes(activeChapterBudget.notes || "");
    }
  }, [activeChapterBudget]);

  const handleNoteChange = useCallback(({ target: { value } }) => {
    setNotes(value);
  }, []);

  const handleFileChange = useCallback(({ target: { files } }) => {
    setSelectedFile(files[0]);
  }, []);

  const handleSave = useCallback(async () => {
    if (isCreate) {
      try {
        if (!selectedFile) {
          throw new Error("No File selected");
        }
        const result = await createChapterBudget({
          chapter: {
            id: user.chapter.id
          },
          year: fiscalYear,
          notes
        });
        if (result.isSuccess) {
          toastr.success("Budget created successfully");
        }
        const formData = new FormData();
        formData.append("file", selectedFile, selectedFile.name);
        const fileResult = await updateChapterBudgetFile(
          result.item.id,
          formData
        );
        if (fileResult === true) {
          toastr.success("File uploaded successfully");
        }
        history.push("/chapter/budget");
      } catch (err) {
        toastr.error(err);
      }
    } else {
      try {
        const result = await updateChapterBudget({
          id,
          notes,
          chapter: {
            id: budget.chapter.id
          },
          year: budget.year
        });
        if (result === true) {
          toastr.success("Budget updated successfully");
        }
        if (selectedFile) {
          const formData = new FormData();
          formData.append("file", selectedFile, selectedFile.name);
          const fileResult = await updateChapterBudgetFile(id, formData);
          if (fileResult === true) {
            toastr.success("File uploaded successfully");
          }
        }
        history.push("/chapter/budget");
      } catch (err) {
        toastr.error(err);
      }
    }
  }, [id, notes, selectedFile, fiscalYear, history, budget]);

  const getFiscalYear = useCallback(() => {
    return isCreate ? fiscalYear : budget && budget.year;
  }, [id, fiscalYear, budget]);

  const handleCancel = useCallback(() => {
    history.push("/chapter/budget");
  }, [history]);

  const handleDownloadFile = useCallback(
    filename => () => {
      downloadChapterBudgetFile(filename);
    },
    [downloadChapterBudgetFile]
  );

  return (
    <Container>
      <Card>
        <CardHeader color="primary" text>
          <CardText color="primary">
            <Typography variant="h4">
              Chapter Budget -{" "}
              {isCreate ? user.chapter.name : budget && budget.chapter.name}
            </Typography>
          </CardText>
        </CardHeader>
        <CardBody>
          <Paper className={classes.paper}>
            <Grid container justify="center">
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between">
                  {isCreate && (
                    <FormControl variant="outlined">
                      <InputLabel id="demo-simple-select-outlined-label">
                        Fiscal Year
                      </InputLabel>
                      <Select
                        labelId="demo-simple-select-outlined-label"
                        value={fiscalYear}
                        onChange={({ target: { value } }) =>
                          setFiscalYear(value)
                        }
                        label="Fiscal Year"
                        MenuProps={{ classes: { paper: classes.menuPaper } }}
                      >
                        {filteredYearList.map(value => (
                          <MenuItem value={value} key={`fiy-${value}`}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>

                <Typography
                  className={clsx(classes.marginBottom, classes.marginTop)}
                >
                  For FY {getFiscalYear()}
                </Typography>
                <Typography className={classes.marginBottom}>
                  <b>Budget File</b>
                </Typography>

                <Grid
                  container
                  direction="column"
                  className={classes.fullWidth}
                >
                  {!isCreate && budget && (
                    <Grid item xs={12}>
                      <b>Current File:</b>
                      <a
                        className={classes.cursorPointer}
                        onClick={handleDownloadFile(budget.filename)}
                      >
                        {budget.filename}
                      </a>
                    </Grid>
                  )}
                  <Grid
                    container
                    justify="left"
                    alignItems="center"
                    className={classes.marginBottom}
                  >
                    <span>
                      <b>{isCreate ? "File" : "Replace with"}:</b>&nbsp;
                      {selectedFile ? selectedFile.name : "No file chosen"}
                    </span>
                    <div>
                      <input
                        className={classes.fileUpload}
                        type="file"
                        accept=".xls,.xlsx,.doc,.docx,.pdf"
                        id="upload-file-button"
                        onChange={handleFileChange}
                      />
                      <label
                        htmlFor="upload-file-button"
                        style={{ marginLeft: "20px" }}
                      >
                        <Fab component="span" size="small">
                          <DescriptionIcon />
                        </Fab>
                      </label>
                    </div>
                  </Grid>
                  <Grid item className={classes.marginBottom}>
                    (PDF, MS Word or MS Excel Only)
                  </Grid>
                  <Grid item xs={12} className={classes.marginBottom}>
                    <TextField
                      label="Notes"
                      multiline
                      rows={5}
                      value={notes}
                      onChange={handleNoteChange}
                      variant="outlined"
                      className={classes.fullWidth}
                    />
                  </Grid>
                  {(isUpdating.CHAPTER_BUDGET ||
                    isUpdating.CHAPTER_BUDGET_FILE) && (
                    <Grid item xs={12}>
                      <LinearProgress />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Grid container justify="space-between">
                      <Button
                        color="success"
                        variant="contained"
                        onClick={handleSave}
                      >
                        Save
                      </Button>
                      <Button
                        color="danger"
                        variant="contained"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </CardBody>
      </Card>
    </Container>
  );
};

const mapStateToProps = state => ({
  isUpdating: state.chapterReducer.isUpdating,
  isFetching: state.chapterReducer.isFetching,
  isFetched: state.chapterReducer.isFetched,
  chapterBudgets: state.chapterReducer.chapterBudgets,
  activeChapterBudget: state.chapterReducer.activeChapterBudget,
  user: state.userReducer.user
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ ...actionCreatorsChapter }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ChapterBudgetForm);
